import React from "react";
import L, { LatLngExpression, LatLngTuple } from "leaflet";

const AGGREGATION_RADIUS_PX = 28;

function resolveMarkerPosition(marker: import("../../data/layers").LayerMarker): LatLngTuple | null {
  if (marker.position) {
    return marker.position;
  }

  if (marker.lat && marker.lon) {
    return [marker.lon, marker.lat];
  }

  return null;
}

function createAggregateIcon(
  iconUrl: string | undefined,
  count: number,
  variant?: "default" | "metrorower"
): L.DivIcon {
  const iconHtml = iconUrl
    ? `<img src="${iconUrl}" alt="" class="leaflet-aggregate-marker__image" />`
    : `<span class="leaflet-aggregate-marker__fallback"></span>`;
  const badgeClassName = variant === "metrorower"
    ? "leaflet-aggregate-marker__badge leaflet-aggregate-marker__badge--metrorower"
    : "leaflet-aggregate-marker__badge";

  return L.divIcon({
    className: "leaflet-aggregate-marker",
    html: `
      <div class="leaflet-aggregate-marker__wrap">
        ${iconHtml}
        <span class="${badgeClassName}">x${count}</span>
      </div>
    `,
    iconSize: [56, 56],
    iconAnchor: [28, 28],
    popupAnchor: [0, -24]
  });
}

function buildAggregatePopup(markers: import("../../data/layers").LayerMarker[]): string {
  return markers
    .map((marker, index) => marker.popupHtml || marker.name || `Punkt ${index + 1}`)
    .join("<br>\n");
}

export function getActiveMarkers(
  layerGroupRef: React.MutableRefObject<L.LayerGroup<any> | undefined>,
  map: L.Map | undefined,
  activeMarkers: import("../../data/layers").LayerMarkers[]
) {
  if (layerGroupRef.current) {
    layerGroupRef.current.clearLayers();

    if (!map) {
      return;
    }

    let defaulticon:L.Icon | undefined;
    //TODO: mało tuatj informacji warto mieć więcej
    //TODO: pierwsze to ikonka - zrobić ikonkę
    activeMarkers.forEach((layer) => {
      let options = {},
      options_base = {}
      if (layer.icon && layer.icon.length > 0) {
        defaulticon = new L.Icon({ iconUrl: layer.icon, iconSize: [40,40] })
      }

      const clusters = layer.markers.reduce((result, marker) => {
        const position = resolveMarkerPosition(marker);
        if (!position) {
          return result;
        }

        const projectedPoint = map.project(L.latLng(position), map.getZoom());
        const existingCluster = result.find((cluster) => cluster.point.distanceTo(projectedPoint) <= AGGREGATION_RADIUS_PX);

        if (existingCluster) {
          existingCluster.markers.push(marker);
          existingCluster.positions.push(position);
          existingCluster.point = L.point(
            (existingCluster.point.x * (existingCluster.positions.length - 1) + projectedPoint.x) / existingCluster.positions.length,
            (existingCluster.point.y * (existingCluster.positions.length - 1) + projectedPoint.y) / existingCluster.positions.length
          );
        } else {
          result.push({
            markers: [marker],
            positions: [position],
            point: projectedPoint
          });
        }

        return result;
      }, [] as Array<{
        markers: import("../../data/layers").LayerMarker[];
        positions: LatLngTuple[];
        point: L.Point;
      }>);

      clusters.forEach((cluster) => {
        const [lat, lon] = cluster.positions.reduce(
          (acc, position) => [acc[0] + position[0], acc[1] + position[1]],
          [0, 0]
        );
        const averagePosition: LatLngTuple = [
          lat / cluster.positions.length,
          lon / cluster.positions.length
        ];

        if (cluster.markers.length === 1) {
          const marker = cluster.markers[0];
          if (marker.icon) {
            options = {
              ...options_base,
              icon: new L.Icon({ iconUrl: marker.icon, iconSize: [40,40] })
            }
          } else if (defaulticon) {
            options = {
              ...options_base,
              icon: defaulticon
            }
          } else {
            options = {
              ...options_base
            }
          }

          const mapMarker = L.marker(averagePosition as LatLngExpression, options);
          const popupContent = marker.popupHtml || marker.name;
          if (popupContent) {
            mapMarker.bindPopup(popupContent);
          }
          if (layerGroupRef.current) {
            mapMarker.addTo(layerGroupRef.current);
          }
          return;
        }

        const aggregateIconUrl = cluster.markers[0]?.icon || layer.icon || undefined;
        const aggregateVariant = layer.key === "metrorower" ? "metrorower" : "default";
        const aggregateMarker = L.marker(averagePosition as LatLngExpression, {
          icon: createAggregateIcon(aggregateIconUrl, cluster.markers.length, aggregateVariant)
        });
        aggregateMarker.bindPopup(buildAggregatePopup(cluster.markers));
        if (layerGroupRef.current) {
          aggregateMarker.addTo(layerGroupRef.current);
        }
      })
    });
  }
}
